import * as Sentry from '@sentry/react'
import React, { FC, useEffect, useState } from 'react'
import ReactPixel from '@bettercart/react-facebook-pixel'
import { Button, Spinner, Text } from '@chakra-ui/react'
import { FcGoogle } from 'react-icons/fc'
import { useGoogleLogin } from '@react-oauth/google'
import { useNavigate } from 'react-router'
import {
  AUTH_REDIRECT_URL,
  ENABLE_META_PIXEL,
  META_PIXEL_ID,
  HOW_TO_URL,
  SUPPORT_EMAIL,
} from '../../../constants'
import AnnouncementsPage from '../../../components/AnnouncementsPage'
import { useAuthContext } from '../../../contexts/AuthContext'
import { trackMetaConversion, generateEventId } from '../../../utils/metaConversionsUtils'

const ExtensionInstalledPage: FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthContext()

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: AUTH_REDIRECT_URL,
  })

  const handleGoogleLoginClick = () => {
    try {
      googleLogin()
    } catch (error) {
      Sentry.captureException(error, {
        tags: { source: 'extension_installed_gsi_failure' },
      })
    }
  }

  useEffect(() => {
    if (ENABLE_META_PIXEL) {
      const eventId = generateEventId()
      // Track server-side conversion first
      trackMetaConversion({ eventName: 'ViewContent', eventId })
      // Then track client-side pixel with same event ID for deduplication
      ReactPixel.init(META_PIXEL_ID)
      ReactPixel.track('ViewContent', {}, { eventID: eventId })
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      console.log('User is authenticated on ExtensionInstalledPage')
      setIsLoading(true)
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  const googleLoginButton = (
    <Button
      mb="4"
      as="a"
      style={{
        backgroundColor: 'black',
        borderRadius: '20px',
        borderColor: '#D5D5D5',
        color: 'white',
        fontWeight: 'normal',
        padding: '0 30px',
      }}
      onClick={handleGoogleLoginClick}
      leftIcon={<FcGoogle />}
    >
      Continue with Google
    </Button>
  )

  if (isLoading) {
    return <Spinner size="xl" />
  }

  return (
    <AnnouncementsPage
      heading={`Sign back in to continue organizing with Skipper`}
      headingMarginBottom="24px"
      headingMaxWidth={'900px'}
      contentMaxWidth={'700px'}
      content={[
        'It looks like you’re signed out. To access your saved sessions and organized tabs, please sign in with Google again.',
      ]}
      contentMarginBottom="3rem"
      widget={googleLoginButton}
    >
      <Text mt="2rem" pb={4} fontStyle="italic" color="#585858">
        Need help? Visit our{' '}
        <a
          href={HOW_TO_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#585858', textDecoration: 'underline' }}
        >
          Getting Started Guide
        </a>{' '}
        or email{' '}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          style={{ color: '#585858', textDecoration: 'underline' }}
        >
          {SUPPORT_EMAIL}
        </a>
      </Text>
    </AnnouncementsPage>
  )
}

export default ExtensionInstalledPage
